import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import { Link } from 'react-router-dom';
import './Balance.css';
import { FaPlus, FaExchangeAlt } from 'react-icons/fa';

import { CURRENCY_EURO } from '../../../../util/Constants';
import { getLocalStore, setLocalStore } from '../../../../util/Helpers';
import Card from '../../../components/Card/Card';
import Cards from '../../../components/Cards/Cards';
import { tempTransactions, tempAccountStats } from '../../../../demo/demo';
import { deleteAccountCampaign, depositAccountBalance, getAccountCampaign, getAccountCampaigns, getAccountAdvertisement, getAccountBalance, createAccountCampaign, grantFirstCampaignPremium, purchaseAccountPremium, resequenceCampaignQueue, setAccountDefaultPaymentMethod, syncAccountPaymentMethods, transferAccountBalance, updateAccountCampaign, updateAccountAdvertisement } from '../../../../services/accountService';

const PER_PAGE = 10
const FINANCE_STORAGE_KEY = 'sadida-member-finances'
const PAYMENT_GATEWAY_URL = 'https://pm.pay.hydrographicspro.com/'
const FILTERS = [
    {value: 'all', label: 'All'},
    {value: 'deposit', label: 'Deposits'},
    {value: 'transfer', label: 'Transfers'},
    {value: 'campaign', label: 'Campaigns'}
]
const TRANSACTION_TYPES = new Set(['deposit', 'transfer', 'campaign'])
const PAID_CAMPAIGN_PLANS = {1: '3 Days', 2: '7 Days', 3: '30 Days'}

const isTrialCampaign = (campaign = {}) => {
    const source = campaign || {}
    return String(source.type || source.data?.type || '').toLowerCase() === 'trial'
}

const normalizeTransactions = (items) => (Array.isArray(items) ? items
    .filter((transaction) => TRANSACTION_TYPES.has(String(transaction?.type || '').toLowerCase()))
    .map((transaction) => ({
        ...transaction,
        id: transaction.id || transaction.uuid,
        type: String(transaction.type).toLowerCase(),
        amount: Number(transaction.amount || 0),
        date: transaction.date || transaction.createdAt?.slice(0, 10) || ''
    })) : null)

const readSavedFinanceState = () => {
    const stored = getLocalStore(FINANCE_STORAGE_KEY)
    if (!stored) return null

    try {
        return JSON.parse(stored)
    } catch (error) {
        return null
    }
}

const formatAmount = (amount, currencySymbol) => `${currencySymbol}${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
})}`

const isValidAmount = (value, minimum = 0, maximum = Number.POSITIVE_INFINITY) => {
    const amount = Number(value)
    return Number.isFinite(amount) && amount > minimum && amount <= maximum
}

const normalizePlanValue = (value, seen) => {
    if (typeof value === 'string') return value.trim() || null
    if (typeof value === 'number') return PAID_CAMPAIGN_PLANS[value] || null
    if (!value || typeof value !== 'object') return null
    if (seen.has(value)) return null
    seen.add(value)

    const directValue = value.name || value.label || value.value || value.planName
    if (directValue !== undefined && directValue !== null) return normalizePlanValue(directValue, seen)
    if (value.budget !== undefined && value.budget !== null) return normalizePlanValue(value.budget, seen)
    if (value.plan !== undefined && value.plan !== null) return normalizePlanValue(value.plan, seen)
    return null
}

const normalizeAdvertisingPlan = (accountBalance = {}) => {
    const directKeys = ['advertisingPlan', 'plan', 'billingPlan', 'planId', 'planName', 'activePlan', 'currentPlan', 'campaignPlan', 'budget']
    const nestedKeys = ['data', 'campaign', 'activeCampaign', 'currentCampaign', 'advertisement', 'activeAdvertisement', 'ad', 'subscription', 'billing']
    const pending = [accountBalance]
    const seen = new Set()

    while (pending.length) {
        const current = pending.shift()
        if (!current || typeof current !== 'object' || seen.has(current)) continue
        seen.add(current)

        for (const key of directKeys) {
            if (current[key] !== undefined && current[key] !== null) {
                const plan = normalizePlanValue(current[key], new Set())
                if (plan) return plan
            }
        }

        nestedKeys.forEach((key) => {
            if (current[key] && typeof current[key] === 'object') pending.push(current[key])
        })
    }

    return null
}

const premiumBalanceAfterPurchase = (premium) => {
    const value = Number(premium?.advertisingBalanceAfterPurchase)
    return Number.isFinite(value) && value >= 0 ? value : null
}

const CAMPAIGN_LIST_STATUSES = [
    {value: 'active', label: 'Active'},
    {value: 'pending', label: 'Pending'},
    {value: 'completed', label: 'Completed'}
]

const campaignListStatus = (campaign = {}) => {
    const status = String(campaign.status || '').toLowerCase()
    if (status === 'active' || status === 'auto') return 'active'
    if (status === 'pending') return 'pending'
    return 'completed'
}

const isActiveCampaignRecord = (campaign = {}) => ['active', 'auto'].includes(String(campaign.status || '').toLowerCase())
const isInactiveAdvertisement = (advertisement = {}) => ['inactive', 'break', 'paused'].includes(String(advertisement.status || '').toLowerCase())

const campaignListName = (campaign = {}) => campaign.name || campaign.title || campaign.model?.name || campaign.modelName || campaign.modelSlug || campaign.uuid || 'Advertisement campaign'
const campaignListDate = (value) => value ? new Date(value).toLocaleDateString() : null

const FinanceCampaignList = ({campaigns, loading, error, currencySymbol, onDelete, deletingCampaignUuid}) => {
    const [filter, setFilter] = useState('all')
    const [page, setPage] = useState(0)
    const items = Array.isArray(campaigns) ? campaigns : []
    const visibleItems = items
    const counts = CAMPAIGN_LIST_STATUSES.reduce((result, status) => ({...result, [status.value]: visibleItems.filter((campaign) => campaignListStatus(campaign) === status.value).length}), {})
    const visibleCampaigns = filter === 'all' ? visibleItems : visibleItems.filter((campaign) => campaignListStatus(campaign) === filter)
    const totalPages = Math.max(1, Math.ceil(visibleCampaigns.length / PER_PAGE))
    const currentPage = Math.min(page, totalPages - 1)
    const paginatedCampaigns = visibleCampaigns.slice(currentPage * PER_PAGE, (currentPage + 1) * PER_PAGE)
    const handleFilter = (nextFilter) => {
        setFilter(nextFilter)
        setPage(0)
    }

    return (
        <section className="finance-campaigns" aria-labelledby="finance-campaigns-title">
            <div className="section-title-row">
                <p id="finance-campaigns-title">Campaigns</p>
            </div>
            {loading && <div className="balance-api-status campaign-list-status" role="status">Loading campaigns…</div>}
            {!loading && error && <div className="balance-api-status balance-api-error campaign-list-status" role="alert">{error}</div>}
            {!loading && !error && <>
                <div className="campaign-list-filters" role="group" aria-label="Campaign status filters">
                    <button type="button" className={filter === 'all' ? 'filter-btn active' : 'filter-btn'} aria-pressed={filter === 'all'} onClick={() => handleFilter('all')}>All <span>{visibleItems.length}</span></button>
                    {CAMPAIGN_LIST_STATUSES.map((status) => <button type="button" key={status.value} className={filter === status.value ? 'filter-btn active' : 'filter-btn'} aria-pressed={filter === status.value} onClick={() => handleFilter(status.value)}>{status.label} <span>{counts[status.value]}</span></button>)}
                </div>
                {paginatedCampaigns.length > 0 ? paginatedCampaigns.map((campaign) => {
                    const status = campaignListStatus(campaign)
                    const plan = PAID_CAMPAIGN_PLANS[Number(campaign.budget)]
                    const startsAt = campaign.startsAt || campaign.data?.startsAt
                    const expiresAt = campaign.expiresAt || campaign.data?.expiresAt
                    return <div className="finance-campaign-row frosted-glow" key={campaign.uuid || `${campaignListName(campaign)}-${status}`}>
                        <div className="finance-campaign-main">
                            <p className="finance-campaign-name">{campaignListName(campaign)}</p>
                            <div className="finance-campaign-details">
                                {plan && <span>{plan}</span>}
                                {status === 'pending' && startsAt && <span>Starts {campaignListDate(startsAt)}</span>}
                                {expiresAt && <span>Expires {campaignListDate(expiresAt)}</span>}
                                {campaign.spent !== undefined && <span>Spent {formatAmount(campaign.spent, currencySymbol)}</span>}
                            </div>
                        </div>
                        <div className="finance-campaign-actions">
                            <span className={`finance-campaign-status ${status}`}>{status}</span>
                            {status === 'pending' && onDelete && <button type="button" className="finance-campaign-delete" onClick={() => onDelete(campaign)} disabled={deletingCampaignUuid === campaign.uuid} aria-label={`Delete pending campaign ${campaignListName(campaign)}`}>
                                {deletingCampaignUuid === campaign.uuid ? 'Deleting…' : 'Delete'}
                            </button>}
                        </div>
                    </div>
                }) : <p className="finance-empty">No {filter === 'all' ? '' : `${filter} `}campaigns found.</p>}
                {totalPages > 1 && (
                    <div className="tx-pagination" aria-label="Campaign pages">
                        <button type="button" className={currentPage === 0 ? 'page-btn disabled' : 'page-btn'} disabled={currentPage === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>← Prev</button>
                        <span className="page-info">{currentPage + 1} / {totalPages}</span>
                        <button type="button" className={currentPage >= totalPages - 1 ? 'page-btn disabled' : 'page-btn'} disabled={currentPage >= totalPages - 1} onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}>Next →</button>
                    </div>
                )}
            </>}
        </section>
    )
}

const FinanceModal = ({title, onClose, children, className = ''}) => (
    <div className="deposit-modal-overlay" onClick={onClose}>
        <div className={`deposit-modal ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby="finance-modal-title" onClick={(event) => event.stopPropagation()}>
            <div className="deposit-modal-header">
                <h2 id="finance-modal-title">{title}</h2>
                <button type="button" className="deposit-modal-close" onClick={onClose} aria-label="Close dialog">&times;</button>
            </div>
            {children}
        </div>
    </div>
)

const normalizePaymentMethods = (methods) => (Array.isArray(methods) ? methods.map((method) => ({
    ...method,
    id: method.id || method.uuid,
    default: method.default ?? method.isDefault ?? false
})) : null)

const Balance = () => {
    const savedState = useMemo(readSavedFinanceState, [])
    const [availableBalance, setAvailableBalance] = useState(savedState?.availableBalance ?? tempAccountStats.availableBalance)
    const [customerId, setCustomerId] = useState(null)
    const [campaignUuid, setCampaignUuid] = useState(null)
    const [advertisementUuid, setAdvertisementUuid] = useState(null)
    const [campaignStatus, setCampaignStatus] = useState('inactive')
    const [campaigns, setCampaigns] = useState([])
    const [campaignsLoading, setCampaignsLoading] = useState(true)
    const [advertisementLoading, setAdvertisementLoading] = useState(true)
    const [campaignsError, setCampaignsError] = useState('')
    const [campaignDeleteError, setCampaignDeleteError] = useState('')
    const [deletingCampaignUuid, setDeletingCampaignUuid] = useState(null)
    const [advertisingBalance, setAdvertisingBalance] = useState(null)
    const [advertisingPlan, setAdvertisingPlan] = useState(null)
    const [premium, setPremium] = useState(null)
    const premiumBalanceOverride = useRef(null)
    const advertisingPlanPriority = useRef(0)
    const [campaignSpend, setCampaignSpend] = useState(null)
    const [transactions, setTransactions] = useState(normalizeTransactions(savedState?.transactions ?? tempTransactions) || [])
    const [paymentMethods, setPaymentMethods] = useState([])
    const [balanceLoading, setBalanceLoading] = useState(true)
    const [balanceError, setBalanceError] = useState('')

    const [filter, setFilter] = useState('all')
    const [page, setPage] = useState(0)
    const [showDeposit, setShowDeposit] = useState(false)
    const [depositAmount, setDepositAmount] = useState('')
    const [depositStep, setDepositStep] = useState('amount')
    const [depositMethod, setDepositMethod] = useState(null)
    const [depositSubmitting, setDepositSubmitting] = useState(false)
    const [depositError, setDepositError] = useState('')
    const [showTransfer, setShowTransfer] = useState(false)
    const [transferAmount, setTransferAmount] = useState('')
    const [transferStep, setTransferStep] = useState('amount')
    const [transferError, setTransferError] = useState('')
    const [transferSubmitting, setTransferSubmitting] = useState(false)
    const [showAddCard, setShowAddCard] = useState(false)
    const [paymentMethodsSyncing, setPaymentMethodsSyncing] = useState(false)
    const [defaultPaymentMethodUpdating, setDefaultPaymentMethodUpdating] = useState(null)
    const [paymentMethodError, setPaymentMethodError] = useState('')
    const [manageCard, setManageCard] = useState(null)

    const currencySymbol = CURRENCY_EURO === 'euro' ? '€' : '$'

    const applyAdvertisingPlan = useCallback((plan, priority) => {
        if (!plan || priority < advertisingPlanPriority.current) return
        advertisingPlanPriority.current = priority
        setAdvertisingPlan(plan)
    }, [])

    const loadAccountBalance = useCallback(() => {
        setBalanceLoading(true)
        setBalanceError('')

        return getAccountBalance()
            .then((accountBalance) => {
                setCustomerId(accountBalance.customerId ?? null)
                const resolvedAdvertisingBalance = premiumBalanceOverride.current !== null
                    ? premiumBalanceOverride.current
                    : accountBalance.advertisingBalance
                setAdvertisingBalance(resolvedAdvertisingBalance)
                applyAdvertisingPlan(normalizeAdvertisingPlan(accountBalance), 1)
                setCampaignSpend(accountBalance.campaignSpend ?? null)
                setAvailableBalance(accountBalance.availableBalance)
                const apiPaymentMethods = normalizePaymentMethods(accountBalance.paymentMethods)
                setPaymentMethods(apiPaymentMethods || [])
                const apiTransactions = normalizeTransactions(accountBalance.transactions)
                if (apiTransactions) {
                    setTransactions(apiTransactions)
                    setPage(0)
                }
            })
            .catch((error) => setBalanceError(error.message || 'We could not load your advertising balance.'))
            .finally(() => setBalanceLoading(false))
    }, [applyAdvertisingPlan])

    const loadAccountCampaign = useCallback(() => {
        setCampaignsLoading(true)
        setCampaignsError('')
        const campaignRequest = Promise.resolve(typeof getAccountCampaigns === 'function'
            ? getAccountCampaigns()
            : typeof getAccountCampaign === 'function' ? getAccountCampaign() : null)
            .then((result) => {
                const campaignList = Array.isArray(result) ? result : result ? [result] : []
                setCampaigns(campaignList)
                return campaignList.find(isActiveCampaignRecord) || campaignList.find((item) => normalizeAdvertisingPlan(item) || PAID_CAMPAIGN_PLANS[Number(item?.budget)]) || campaignList[0] || null
            })
            .catch((error) => {
                setCampaigns([])
                setCampaignsError(error.message || 'We could not load your campaigns.')
                return null
            })
        const advertisementRequest = Promise.resolve(typeof getAccountAdvertisement === 'function' ? getAccountAdvertisement() : null)
            .then((advertisement) => {
                setAdvertisementLoading(false)
                return advertisement
            })
            .catch(() => {
                setAdvertisementLoading(false)
                return null
            })

        return Promise.all([campaignRequest, advertisementRequest]).then(([campaign, advertisement]) => {
            setCampaignsLoading(false)
            setAdvertisementUuid(advertisement?.uuid || advertisement?.id || null)
            const loadedPremium = advertisement?.premium || advertisement?.data?.premium || null
            const loadedPremiumBalance = premiumBalanceAfterPurchase(loadedPremium)
            if (loadedPremium) setPremium(loadedPremium)
            if (loadedPremiumBalance !== null) {
                premiumBalanceOverride.current = loadedPremiumBalance
                setAdvertisingBalance(loadedPremiumBalance)
            }
            setCampaignUuid(campaign?.uuid || advertisement?.uuid || null)
            const campaignStatusValue = campaign?.status || advertisement?.status
            setCampaignStatus(['auto', 'active', 'inactive', 'pending', 'completed'].includes(String(campaignStatusValue || '').toLowerCase()) ? String(campaignStatusValue).toLowerCase() : 'inactive')
            const advertisementPlan = isInactiveAdvertisement(advertisement) ? null : normalizeAdvertisingPlan(advertisement)
            const paidPlan = PAID_CAMPAIGN_PLANS[Number(campaign?.budget)]
            const resolvedPlan = advertisementPlan || paidPlan || (isTrialCampaign(advertisement || campaign) ? 'Trial' : normalizeAdvertisingPlan(campaign))
            if (resolvedPlan) {
                applyAdvertisingPlan(resolvedPlan, 2)
            }
        })
    }, [applyAdvertisingPlan])

    const handlePremiumUpgrade = useCallback(async (details = {}) => {
        if (typeof purchaseAccountPremium !== 'function' || !advertisementUuid) {
            throw new Error('We could not identify your model profile. Please try again.')
        }
        const savedAdvertisement = await purchaseAccountPremium(advertisementUuid, details)
        const savedPremium = savedAdvertisement?.premium || savedAdvertisement?.data?.premium || null
        const requestedBalanceAfterPurchase = Number(details.advertisingBalanceAfterPurchase)
        const savedBalanceAfterPurchase = premiumBalanceAfterPurchase(savedPremium)
        const resolvedBalanceAfterPurchase = Number.isFinite(requestedBalanceAfterPurchase)
            ? requestedBalanceAfterPurchase
            : savedBalanceAfterPurchase
        setPremium(savedPremium)
        if (resolvedBalanceAfterPurchase !== null) {
            premiumBalanceOverride.current = resolvedBalanceAfterPurchase
            setAdvertisingBalance(resolvedBalanceAfterPurchase)
        } else if (savedAdvertisement?.advertisingBalance !== undefined) {
            setAdvertisingBalance(Number(savedAdvertisement.advertisingBalance))
        }
        return savedAdvertisement
    }, [advertisementUuid])

    const handlePlanChange = useCallback(async (plan) => {
        const budget = Number(({ '3 Days': 1, '7 Days': 2, '30 Days': 3 })[plan] || plan)
        const activeCampaign = campaigns.find(isActiveCampaignRecord)
        const currentPlan = advertisingPlan || normalizeAdvertisingPlan(activeCampaign)
        const hasLiveCampaign = Boolean(activeCampaign) || ['active', 'auto'].includes(campaignStatus)
        const hasExistingPlan = Boolean(currentPlan)
        const isAutoPayCampaign = activeCampaign?.status === 'auto' || (campaignStatus === 'auto' && Boolean(activeCampaign))

        if (hasLiveCampaign && hasExistingPlan && isAutoPayCampaign && typeof updateAccountCampaign === 'function') {
            const currentCampaignUuid = activeCampaign?.uuid || campaignUuid
            if (!currentCampaignUuid) throw new Error('We could not identify your campaign. Please try again.')
            const savedCampaign = await updateAccountCampaign(currentCampaignUuid, {budget})
            setCampaigns((current) => current.map((campaign) => campaign.uuid === currentCampaignUuid ? {...campaign, ...savedCampaign} : campaign))
            setCampaignUuid(savedCampaign?.uuid || currentCampaignUuid)
            setCampaignStatus(savedCampaign?.status || 'auto')
            const persistedPlan = savedCampaign?.budget ? ({1: '3 Days', 2: '7 Days', 3: '30 Days'})[Number(savedCampaign.budget)] : plan
            applyAdvertisingPlan(persistedPlan, 3)
            return persistedPlan
        }

        if (hasLiveCampaign && hasExistingPlan && typeof createAccountCampaign === 'function') {
            const savedCampaign = await createAccountCampaign({budget, status: 'pending'})
            if (savedCampaign) {
                setCampaigns((current) => [
                    ...current.filter((campaign) => campaign.uuid !== savedCampaign.uuid),
                    savedCampaign
                ])
            }
            // The active campaign remains current; the new campaign is only the next one.
            return currentPlan || plan
        }

        if (!hasExistingPlan && !campaigns.length && !campaignsError && advertisementUuid && typeof createAccountCampaign === 'function') {
            const savedCampaign = await createAccountCampaign({budget, status: 'active'})
            if (savedCampaign) {
                setCampaigns((current) => [
                    ...current.filter((campaign) => campaign.uuid !== savedCampaign.uuid),
                    savedCampaign
                ])
                setCampaignUuid(savedCampaign.uuid || campaignUuid)
                setCampaignStatus(savedCampaign.status || 'active')
            }
            const persistedPlan = savedCampaign?.budget
                ? ({1: '3 Days', 2: '7 Days', 3: '30 Days'})[Number(savedCampaign.budget)] || plan
                : plan
            if (!premium && typeof grantFirstCampaignPremium === 'function') {
                const grantedPremium = await grantFirstCampaignPremium(advertisementUuid)
                const savedPremium = grantedPremium?.premium || grantedPremium?.data?.premium || null
                if (savedPremium) setPremium(savedPremium)
            }
            applyAdvertisingPlan(persistedPlan, 3)
            return persistedPlan
        }

        if (typeof updateAccountCampaign === 'function') {
            const savedCampaign = campaignUuid
                ? await updateAccountCampaign(campaignUuid, {budget})
                : await createAccountCampaign({budget, status: campaignStatus})
            setCampaignUuid(savedCampaign?.uuid || campaignUuid)
            setCampaignStatus(savedCampaign?.status || campaignStatus)
            const persistedPlan = savedCampaign?.budget ? ({1: '3 Days', 2: '7 Days', 3: '30 Days'})[Number(savedCampaign.budget)] : plan
            applyAdvertisingPlan(persistedPlan, 3)
            return persistedPlan
        }
        if (!campaignUuid) throw new Error('We could not identify your advertisement. Please try again.')
        const savedAdvertisement = await updateAccountAdvertisement(campaignUuid, {plan})
        const persistedPlan = normalizeAdvertisingPlan(savedAdvertisement) || plan
        applyAdvertisingPlan(persistedPlan, 3)
        return persistedPlan
    }, [advertisingPlan, campaigns, campaignsError, campaignUuid, advertisementUuid, campaignStatus, premium, applyAdvertisingPlan])

    const handleDeleteCampaign = useCallback(async (campaign) => {
        if (!campaign?.uuid || campaignListStatus(campaign) !== 'pending' || typeof deleteAccountCampaign !== 'function' || deletingCampaignUuid) return

        setDeletingCampaignUuid(campaign.uuid)
        setCampaignDeleteError('')
        try {
            const result = await deleteAccountCampaign(campaign.uuid)
            const remainingCampaigns = current => current.filter((item) => String(item.uuid) !== String(campaign.uuid))
            setCampaigns((current) => {
                if (Array.isArray(result?.campaigns)) return result.campaigns
                const remaining = remainingCampaigns(current)
                const resequenced = typeof resequenceCampaignQueue === 'function' ? resequenceCampaignQueue(remaining) : remaining
                return Array.isArray(resequenced) ? resequenced : remaining
            })
        } catch (error) {
            setCampaignDeleteError(error.message || 'We could not delete your campaign.')
        } finally {
            setDeletingCampaignUuid(null)
        }
    }, [deletingCampaignUuid])

    const handleCampaignStatusChange = useCallback(async (status) => {
        if (typeof updateAccountCampaign !== 'function' || !campaignUuid) {
            setCampaignStatus(status)
            return
        }
        const savedCampaign = await updateAccountCampaign(campaignUuid, {status})
        setCampaignStatus(savedCampaign?.status || status)
    }, [campaignUuid])

    useEffect(() => {
        loadAccountBalance()
        loadAccountCampaign()
    }, [loadAccountBalance, loadAccountCampaign])

    useEffect(() => {
        setLocalStore(FINANCE_STORAGE_KEY, {
            availableBalance,
            transactions,
            paymentMethods
        }, true)
    }, [availableBalance, transactions, paymentMethods])

    const filteredTransactions = filter === 'all'
        ? transactions
        : transactions.filter((transaction) => transaction.type === filter)
    const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PER_PAGE))
    const paginatedTransactions = filteredTransactions.slice(page * PER_PAGE, (page + 1) * PER_PAGE)

    const handleFilter = (nextFilter) => {
        setFilter(nextFilter)
        setPage(0)
    }

    const prependTransaction = (transaction) => {
        const normalizedTransaction = normalizeTransactions([transaction])?.[0]
        if (!normalizedTransaction) return

        setTransactions((current) => [normalizedTransaction, ...current])
        setPage(0)
    }

    const closeDeposit = (force = false) => {
        if (depositSubmitting && !force) return
        setShowDeposit(false)
        setDepositStep('amount')
        setDepositAmount('')
        setDepositMethod(null)
        setDepositError('')
    }

    const openDeposit = () => {
        setDepositAmount('')
        setDepositStep('amount')
        setDepositMethod(null)
        setDepositError('')
        setShowDeposit(true)
    }

    const handleDepositConfirm = async () => {
        const amount = Number(depositAmount)
        if (!depositMethod || !isValidAmount(depositAmount) || depositSubmitting) return

        setDepositSubmitting(true)
        setDepositError('')
        try {
            const result = await depositAccountBalance(amount, depositMethod.id)
            setAvailableBalance(result.availableBalance)
            if (result.transaction) prependTransaction(result.transaction)
            closeDeposit(true)
        } catch (error) {
            setDepositError(error.message || 'We could not process your deposit.')
        } finally {
            setDepositSubmitting(false)
        }
    }

    const openTransfer = () => {
        setTransferAmount('')
        setTransferStep('amount')
        setTransferError('')
        setShowTransfer(true)
    }

    const closeTransfer = (force = false) => {
        if (transferSubmitting && !force) return
        setShowTransfer(false)
        setTransferAmount('')
        setTransferStep('amount')
        setTransferError('')
    }

    const handleTransferConfirm = async () => {
        if (!isValidAmount(transferAmount, 0, availableBalance) || advertisingBalance === null) return

        setTransferSubmitting(true)
        setTransferError('')
        try {
            const result = await transferAccountBalance(Number(transferAmount))
            setAvailableBalance(result.availableBalance)
            setAdvertisingBalance(result.advertisingBalance)
            if (result.transaction) {
                prependTransaction({
                    ...result.transaction,
                    id: result.transaction.id || result.transaction.uuid || Date.now(),
                    type: 'transfer'
                })
            }
            closeTransfer(true)
        } catch (error) {
            setTransferError(error.message || 'We could not transfer funds to your advertising balance.')
        } finally {
            setTransferSubmitting(false)
        }
    }

    const openAddCardGateway = () => {
        closeDeposit()
        setShowAddCard(true)
    }

    const closeAddCardGateway = () => {
        setShowAddCard(false)
        setPaymentMethodsSyncing(true)
        syncAccountPaymentMethods()
            .catch(() => null)
            .then(loadAccountBalance)
            .finally(() => setPaymentMethodsSyncing(false))
    }

    const paymentGatewayUrl = customerId
        ? `${PAYMENT_GATEWAY_URL}?cus_id=${encodeURIComponent(customerId)}`
        : null

    const handleSetDefault = async (id) => {
        setDefaultPaymentMethodUpdating(id)
        setPaymentMethodError('')
        try {
            const accountBalance = await setAccountDefaultPaymentMethod(id)
            const apiPaymentMethods = normalizePaymentMethods(accountBalance?.paymentMethods)
            if (apiPaymentMethods) {
                setPaymentMethods(apiPaymentMethods)
            } else {
                setPaymentMethods((current) => current.map((method) => ({...method, default: String(method.id) === String(id)})))
            }
            setManageCard(null)
        } catch (error) {
            setPaymentMethodError(error.message || 'We could not update your default payment method.')
        } finally {
            setDefaultPaymentMethodUpdating(null)
        }
    }

    const handleDeleteCard = (id) => {
        setPaymentMethods((current) => {
            const remaining = current.filter((method) => method.id !== id)
            if (remaining.length > 0 && !remaining.some((method) => method.default)) {
                remaining[0] = {...remaining[0], default: true}
            }
            return remaining
        })
        setManageCard(null)
    }

    const canTransfer = advertisingBalance !== null && isValidAmount(transferAmount, 0, availableBalance)
    const canDeposit = isValidAmount(depositAmount)
    const activeCampaign = campaigns.find(isActiveCampaignRecord)
    const hasActiveAutoPay = activeCampaign?.status === 'auto' || (campaignStatus === 'auto' && Boolean(activeCampaign))
    const pendingPlanChange = !hasActiveAutoPay && (Boolean(activeCampaign) || ['active', 'auto'].includes(campaignStatus)) && Boolean(advertisingPlan || normalizeAdvertisingPlan(activeCampaign))

    return (
        <section className="section-header account-page balance-page">
            <div className="view-container">
                <h1>Balance<span>Finances</span></h1>
                <div className="balance-account-layout">
                    <nav className="dashboard-nav balance-nav" aria-label="Member area">
                        <Link to={'/account'} className="dash-nav-item">Dashboard</Link>
                        <Link to={'/account/balance'} className="dash-nav-item active" aria-current="page">Finances</Link>
                        <Link to={'/account/ad'} className="dash-nav-item">Advertisement</Link>
                        <Link to={'/account/store'} className="dash-nav-item">Store</Link>
                    </nav>
                    <div className="dashboard-content balance-content">
                        <div className="balance-overview">
                            <div className="bal-stat frosted-glow">
                                <div className="bal-stat-label">
                                    Available Balance
                                    <span className="bal-stat-icons">
                                        <button type="button" className="bal-icon-wrap" onClick={openDeposit} aria-label="Deposit funds">
                                            <span className="bal-transfer-icon"><FaPlus size={10} /></span>
                                            <span className="bal-icon-desc">Deposit</span>
                                        </button>
                                        <button type="button" className="bal-icon-wrap" onClick={openTransfer} aria-label="Transfer to advertising balance">
                                            <span className="bal-transfer-icon"><FaExchangeAlt size={10} /></span>
                                            <span className="bal-icon-desc">Transfer</span>
                                        </button>
                                    </span>
                                </div>
                                <div className="bal-stat-value">{formatAmount(availableBalance, currencySymbol)}</div>
                            </div>
                            <div className="bal-stat frosted-glow">
                                <div className="bal-stat-label">Campaign Spend</div>
                                <div className="bal-stat-value negative">{campaignSpend === null ? '—' : formatAmount(campaignSpend, currencySymbol)}</div>
                            </div>
                        </div>

                        <div className="balance-layout">
                    <div className="balance-left">
                        {balanceLoading && <div className="balance-api-status" role="status">Loading advertising balance…</div>}
                        {!balanceLoading && balanceError && (
                            <div className="balance-api-status balance-api-error" role="alert">
                                <span>{balanceError}</span>
                                <button type="button" className="section-btn" onClick={loadAccountBalance}>Retry</button>
                            </div>
                        )}
                        {!balanceLoading && !advertisementLoading && !balanceError && advertisingBalance !== null && (
                            <Card
                                currency={CURRENCY_EURO}
                                adBalance={advertisingBalance}
                                availableBalance={availableBalance}
                                paymentMethods={paymentMethods}
                                premium={premium}
                                modelUuid={advertisementUuid}
                                onPremiumUpgrade={handlePremiumUpgrade}
                                plan={advertisingPlan}
                                campaignStatus={campaignStatus}
                                deferPlanBilling={pendingPlanChange}
                                planChangeWithoutCharge={hasActiveAutoPay}
                                onPlanChange={handlePlanChange}
                                onCampaignStatusChange={handleCampaignStatusChange}
                                onAddCard={openAddCardGateway}
                            />
                        )}

                        {campaignDeleteError && <p className="balance-api-status balance-api-error" role="alert">{campaignDeleteError}</p>}
                        <FinanceCampaignList campaigns={campaigns} loading={campaignsLoading} error={campaignsError} currencySymbol={currencySymbol} onDelete={handleDeleteCampaign} deletingCampaignUuid={deletingCampaignUuid} />

                        <div className="payment-methods">
                            <div className="section-title-row">
                                <p>Payment Methods</p>
                                <button type="button" className="section-btn" onClick={openAddCardGateway}>Add New</button>
                            </div>
                            {paymentMethodsSyncing && <div className="balance-api-status payment-methods-loading" role="status" aria-live="polite">Loading payment methods…</div>}
                            {paymentMethods.length > 0 ? paymentMethods.map((method) => (
                                <div className="payment-method frosted-glow" key={method.id}>
                                    <div className="pm-left">
                                        <span className="pm-card-brand" aria-hidden="true"><Cards type={method.type} size={30} /></span>
                                        <span className="pm-number">**** {method.last4}</span>
                                        <span className="pm-expiry">Exp {method.expiry}</span>
                                    </div>
                                    <div className="pm-right">
                                        {method.default && <span className="pm-default">Default</span>}
                                        <button type="button" className="pm-manage" onClick={() => { setPaymentMethodError(''); setManageCard(method) }}>Manage</button>
                                    </div>
                                </div>
                            )) : <p className="finance-empty">No payment methods saved. Add one to deposit funds.</p>}
                        </div>
                    </div>

                    <div className="balance-right">
                        <div className="balance-transactions">
                            <div className="section-title-row"><p>Transactions</p></div>
                            <div className="tx-filters" role="group" aria-label="Filter transactions">
                                {FILTERS.map((transactionFilter) => (
                                    <button type="button" key={transactionFilter.value} className={filter === transactionFilter.value ? 'filter-btn active' : 'filter-btn'} onClick={() => handleFilter(transactionFilter.value)} aria-pressed={filter === transactionFilter.value}>{transactionFilter.label}</button>
                                ))}
                            </div>
                            {paginatedTransactions.length > 0 ? paginatedTransactions.map((transaction) => (
                                <div className="transaction" key={transaction.id || transaction.uuid}>
                                    <div className="tx-left"><div className="tx-desc">{transaction.description}</div><div className="tx-date">{transaction.date}</div></div>
                                    <div className={'tx-amount' + (transaction.amount >= 0 ? ' positive' : ' negative')}>
                                        {transaction.amount >= 0 ? '+' : '-'}{formatAmount(Math.abs(transaction.amount), currencySymbol)}
                                    </div>
                                </div>
                            )) : <p className="finance-empty">No transactions match this filter.</p>}
                            {totalPages > 1 && (
                                <div className="tx-pagination" aria-label="Transaction pages">
                                    <button type="button" className={page === 0 ? 'page-btn disabled' : 'page-btn'} disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>← Prev</button>
                                    <span className="page-info">{page + 1} / {totalPages}</span>
                                    <button type="button" className={page >= totalPages - 1 ? 'page-btn disabled' : 'page-btn'} disabled={page >= totalPages - 1} onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}>Next →</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                    </div>
                </div>
            </div>

            {showDeposit && (
                <FinanceModal title={depositStep === 'amount' ? 'Deposit Funds' : depositStep === 'card' ? 'Select Payment' : 'Summary'} onClose={closeDeposit}>
                    {depositStep === 'amount' && <>
                        <p className="deposit-desc">Enter the amount you want to deposit.</p>
                        <div className="deposit-input"><span className="deposit-currency">{currencySymbol}</span><input type="number" min="1" step="0.01" placeholder="0.00" aria-label="Deposit amount" value={depositAmount} onChange={(event) => setDepositAmount(event.target.value)} /></div>
                        <div className="deposit-presets">{[50, 100, 250, 500, 1000].map((amount) => <button type="button" key={amount} className="deposit-preset" onClick={() => setDepositAmount(amount.toString())}>{currencySymbol}{amount}</button>)}</div>
                        <button type="button" className={canDeposit ? 'deposit-submit' : 'deposit-submit disabled'} disabled={!canDeposit} onClick={() => setDepositStep('card')}>Continue</button>
                    </>}
                    {depositStep === 'card' && <>
                        <p className="deposit-desc">Depositing {formatAmount(depositAmount, currencySymbol)} — select a payment method.</p>
                        <div className="deposit-methods">
                            {paymentMethods.map((method) => <button type="button" className="deposit-method" key={method.id} onClick={() => { setDepositMethod(method); setDepositError(''); setDepositStep('summary') }}><Cards type={method.type} size={25} /><span className="deposit-method-num">**** {method.last4}</span><span className="deposit-method-exp">Exp {method.expiry}</span>{method.default && <span className="deposit-method-default">Default</span>}</button>)}
                            <button type="button" className="deposit-method add-new" onClick={openAddCardGateway}>+ Add New Card</button>
                        </div>
                        <button type="button" className="deposit-back" onClick={() => setDepositStep('amount')}>← Back</button>
                    </>}
                    {depositStep === 'summary' && <>
                        <p className="deposit-desc">Please review and confirm your deposit.</p>
                        <div className="summary-details"><div className="summary-row"><span className="summary-label">Deposit Amount</span><span className="summary-value">{formatAmount(depositAmount, currencySymbol)}</span></div><div className="summary-row"><span className="summary-label">Payment Method</span><span className="summary-value">**** {depositMethod?.last4}</span></div><div className="summary-row"><span className="summary-label">New Balance</span><span className="summary-value">{formatAmount(availableBalance + Number(depositAmount), currencySymbol)}</span></div></div>
                        {depositError && <p className="transfer-error" role="alert">{depositError}</p>}
                        <button type="button" className="deposit-submit" disabled={depositSubmitting} onClick={handleDepositConfirm}>{depositSubmitting ? 'Processing…' : 'Confirm & Pay'}</button><button type="button" className="deposit-back" disabled={depositSubmitting} onClick={() => setDepositStep('card')}>← Back</button>
                    </>}
                </FinanceModal>
            )}

            {manageCard && <FinanceModal title="Manage Card" onClose={() => setManageCard(null)}>
                <div className="manage-card-info"><Cards type={manageCard.type} size={40} /><div className="manage-card-details"><span className="manage-card-num">**** {manageCard.last4}</span><span className="manage-card-exp">Expires {manageCard.expiry}</span></div></div>
                {paymentMethodError && <p className="transfer-error" role="alert">{paymentMethodError}</p>}
                <div className="manage-card-actions">{!manageCard.default && <button type="button" className="deposit-submit" disabled={defaultPaymentMethodUpdating === manageCard.id} onClick={() => handleSetDefault(manageCard.id)}>{defaultPaymentMethodUpdating === manageCard.id ? 'Saving…' : 'Set as Default'}</button>}<button type="button" className="manage-card-delete" onClick={() => handleDeleteCard(manageCard.id)}>Delete Card</button></div>
            </FinanceModal>}

            {showAddCard && <FinanceModal title="Add New Card" className="payment-gateway-modal" onClose={closeAddCardGateway}>
                {balanceLoading && <p className="deposit-desc" role="status">Loading payment setup…</p>}
                {!balanceLoading && paymentGatewayUrl && <iframe className="payment-gateway-iframe" title="Add a payment method" src={paymentGatewayUrl} loading="lazy" allow="payment" />}
                {!balanceLoading && !paymentGatewayUrl && <p className="transfer-error" role="alert">We could not find a customer payment profile for this account.</p>}
            </FinanceModal>}

            {showTransfer && <FinanceModal title={transferStep === 'amount' ? 'Transfer to Ad Balance' : 'Summary'} onClose={closeTransfer}>
                {transferStep === 'amount' && <>
                    <p className="deposit-desc">Transfer from your available balance ({formatAmount(availableBalance, currencySymbol)}) to your advertising balance.</p>
                    <div className="deposit-input"><span className="deposit-currency">{currencySymbol}</span><input type="number" min="0.01" max={availableBalance} step="0.01" placeholder="0.00" aria-label="Transfer amount" value={transferAmount} onChange={(event) => setTransferAmount(event.target.value)} /></div>
                    <div className="deposit-presets">{[50, 100, 250, 500].filter((amount) => amount <= availableBalance).map((amount) => <button type="button" key={amount} className="deposit-preset" onClick={() => setTransferAmount(amount.toString())}>{currencySymbol}{amount}</button>)}<button type="button" className="deposit-preset" onClick={() => setTransferAmount(availableBalance.toString())}>Max</button></div>
                    {transferError && <p className="transfer-error" role="alert">{transferError}</p>}
                    {!transferError && transferAmount && Number(transferAmount) > availableBalance && <p className="transfer-error" role="alert">Amount exceeds available balance.</p>}
                    <button type="button" className={canTransfer ? 'deposit-submit' : 'deposit-submit disabled'} disabled={!canTransfer || transferSubmitting} onClick={() => setTransferStep('summary')}>{transferSubmitting ? 'Loading…' : 'Continue'}</button>
                </>}
                {transferStep === 'summary' && <>
                    <p className="deposit-desc">Please review and confirm your transfer.</p>
                    <div className="summary-details"><div className="summary-row"><span className="summary-label">Transfer Amount</span><span className="summary-value">{formatAmount(transferAmount, currencySymbol)}</span></div><div className="summary-row"><span className="summary-label">From</span><span className="summary-value">Available Balance</span></div><div className="summary-row"><span className="summary-label">To</span><span className="summary-value">Advertising Balance</span></div><div className="summary-row"><span className="summary-label">New Available Balance</span><span className="summary-value">{formatAmount(availableBalance - Number(transferAmount), currencySymbol)}</span></div><div className="summary-row"><span className="summary-label">New Advertising Balance</span><span className="summary-value">{formatAmount(advertisingBalance + Number(transferAmount), currencySymbol)}</span></div></div>
                    {transferError && <p className="transfer-error" role="alert">{transferError}</p>}
                    <button type="button" className="deposit-submit" disabled={transferSubmitting} onClick={handleTransferConfirm}>{transferSubmitting ? 'Transferring…' : 'Confirm & Transfer'}</button><button type="button" className="deposit-back" onClick={() => setTransferStep('amount')}>← Back</button>
                </>}
            </FinanceModal>}
        </section>
    )
}

export default Balance
